import { askConfirm } from '@/components/ui/ConfirmHost';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { PinDialog } from '@/components/ui/PinDialog';
import { call, type Meta } from '@/api/desk';

/* ---------- helpers ---------- */
export function errMsg(e: any): string {
  const r = e?.response;
  if (r?.status === 501) return 'This feature is not available yet on the server.';
  if (r?.status === 428) return 'You have not set a transaction PIN yet.';
  if (r?.status === 423) return 'Your PIN is locked. Try again later.';
  const d = r?.data;
  if (d?.errors && typeof d.errors === 'object') {
    const first = Object.values(d.errors as Record<string, any>)[0];
    const m = Array.isArray(first) ? first[0] : first;
    if (m && (!d.message || /given data was invalid|validation/i.test(d.message))) return String(m);
  }
  return d?.message ?? e?.message ?? 'Something went wrong';
}
export const isNA = (e: any) => e?.response?.status === 501;
export const newKey = () => crypto.randomUUID();
export const today = () => new Date().toISOString().slice(0, 10);
export const money = (n: any) => '₦' + Number(n ?? 0).toLocaleString('en-NG', { maximumFractionDigits: 2 });
export const dt = (d?: string | null) => (d ? new Date(d).toLocaleString() : '—');
export const dOnly = (d?: string | null) => (d ? String(d).slice(0, 10) : '—');
export const arr = (x: any): any[] => (Array.isArray(x) ? x : Array.isArray(x?.data) ? x.data : []);
export function useBase() {
  const { pathname } = useLocation();
  return '/' + pathname.split('/')[1];
}

/* ---------- layout ---------- */
export function PageHeader({ title, sub, action }: { title: string; sub?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
      <div>
        <h1 className="text-2xl font-bold">{title}</h1>
        {sub && <p className="text-sm text-slate-500">{sub}</p>}
      </div>
      <div className="flex gap-2 flex-wrap">{action}</div>
    </div>
  );
}

export function Notice({ kind = 'info', children }: { kind?: 'info' | 'error' | 'ok'; children: ReactNode }) {
  const c = kind === 'error' ? 'bg-red-50 text-red-700 border-red-200' : kind === 'ok' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-800 border-amber-200';
  return <div className={`border rounded-xl px-3 py-2 text-sm mb-3 ${c}`}>{children}</div>;
}

export function Tabs({ tabs, value, onChange }: { tabs: { id: string; label: string }[]; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex gap-1 overflow-x-auto border-b border-slate-200 mb-4 -mx-1 px-1">
      {tabs.map((t) => (
        <button key={t.id} onClick={() => onChange(t.id)} className={`px-3 py-2 text-sm font-medium -mb-px border-b-2 whitespace-nowrap shrink-0 ${value === t.id ? 'border-brand-600 text-brand-600' : 'border-transparent text-slate-500'}`}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center sm:p-4">
      <div className={`bg-white rounded-t-2xl sm:rounded-card p-4 sm:p-5 w-full ${wide ? 'sm:max-w-3xl' : 'sm:max-w-md'} max-h-[92dvh] sm:max-h-[90vh] overflow-y-auto`}>
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold">{title}</h3>
          <button aria-label="Close" onClick={onClose} className="text-slate-400 text-xl leading-none">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export const inputCls = 'border rounded-xl w-full px-3 py-2 text-sm bg-white';

/* ---------- table ---------- */
export interface Col<T> { head: string; cell: (r: T) => ReactNode; right?: boolean }
export function DataTable<T>({ cols, rows, loading, error, empty = 'Nothing to show yet.', onRow }: { cols: Col<T>[]; rows?: T[]; loading?: boolean; error?: any; empty?: string; onRow?: (r: T) => void }) {
  if (loading) return <p className="text-sm text-slate-500 py-6 text-center">Loading…</p>;
  if (error) return <Notice kind={isNA(error) ? 'info' : 'error'}>{errMsg(error)}</Notice>;
  if (!rows || rows.length === 0) return <p className="text-sm text-slate-500 py-6 text-center">{empty}</p>;
  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500">
              {cols.map((c) => (
                <th key={c.head} className={`py-2 pr-3 font-medium ${c.right ? 'text-right' : ''}`}>{c.head}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className={`border-t ${onRow ? 'cursor-pointer hover:bg-slate-50' : ''}`} onClick={onRow ? () => onRow(r) : undefined}>
                {cols.map((c) => (
                  <td key={c.head} className={`py-2 pr-3 ${c.right ? 'text-right' : ''}`}>{c.cell(r)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* phones: each row as a card — first column is the title, the rest label/value */}
      <ul className="md:hidden space-y-2">
        {rows.map((r, i) => (
          <li key={i} onClick={onRow ? () => onRow(r) : undefined} className={`rounded-xl border border-slate-200 p-3 text-sm ${onRow ? 'active:bg-slate-50 cursor-pointer' : ''}`}>
            <div className="font-semibold text-slate-900 break-words">{cols[0]?.cell(r)}</div>
            {cols.length > 1 && (
              <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                {cols.slice(1).map((c) => (
                  <div key={c.head} className="contents">
                    <dt className="text-xs text-slate-500 whitespace-nowrap">{c.head || ' '}</dt>
                    <dd className="text-slate-800 min-w-0 break-words text-right">{c.cell(r)}</dd>
                  </div>
                ))}
              </dl>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

export function Pager({ meta, page, onPage }: { meta?: Meta; page: number; onPage: (p: number) => void }) {
  if (!meta || meta.last_page <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-3 text-sm">
      <span className="text-slate-500">Page {meta.page ?? page} of {meta.last_page} · {meta.total} total</span>
      <div className="flex gap-2">
        <Button variant="ghost" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</Button>
        <Button variant="ghost" disabled={page >= meta.last_page} onClick={() => onPage(page + 1)}>Next</Button>
      </div>
    </div>
  );
}

const clean = (p?: Record<string, unknown>) => Object.fromEntries(Object.entries(p ?? {}).filter(([, v]) => v !== '' && v != null));

/** Paginated (or plain) list query against an envelope endpoint. */
export function useList(key: string, url: string, params: Record<string, unknown> = {}, opts: { enabled?: boolean; pin?: string } = {}) {
  const [page, setPage] = useState(1);
  const c = clean(params);
  const sig = JSON.stringify(c);
  useEffect(() => setPage(1), [sig]);
  const q = useQuery({
    queryKey: [key, url, sig, page, opts.pin ?? ''],
    queryFn: () => call('get', url, { params: { ...c, page }, pin: opts.pin }),
    enabled: opts.enabled ?? true,
    retry: false,
  });
  return { ...q, rows: arr(q.data?.data), meta: q.data?.meta, extra: q.data as any, page, setPage };
}

/** Simple GET query returning envelope data. */
export function useGet<T = any>(key: string, url: string, params?: Record<string, unknown>, enabled = true) {
  const c = clean(params);
  return useQuery({ queryKey: [key, url, JSON.stringify(c)], queryFn: async () => (await call<T>('get', url, { params: c })).data, enabled, retry: false });
}

export const useClasses = () => useGet<any[]>('classes', '/school/classes');
export const useSessions = () => useGet<any[]>('sessions', '/school/sessions');
export function useTerms() {
  const s = useSessions();
  const terms = arr(s.data).flatMap((x: any) => (x.terms ?? []).map((t: any) => ({ ...t, label: `${x.name} · ${t.name}` })));
  return { terms, sessions: arr(s.data), ...s };
}
export const classLabel = (c: any) => (c?.name ? `${c.name}${c.arm ? ' ' + c.arm : ''}` : '—');

/* ---------- form ---------- */
export interface FieldDef {
  name: string;
  label: string;
  type?: 'text' | 'number' | 'date' | 'time' | 'datetime-local' | 'select' | 'textarea' | 'checkbox' | 'password' | 'email' | 'file';
  required?: boolean;
  options?: { value: string | number; label: string }[];
  min?: number;
  max?: number;
  maxLength?: number;
  help?: string;
  show?: (v: Record<string, any>) => boolean;
  pattern?: RegExp;
  patternMsg?: string;
}

export function FormModal({ title, fields, initial = {}, submitLabel = 'Save', onSubmit, onClose, wide }: { title: string; fields: FieldDef[]; initial?: Record<string, any>; submitLabel?: string; onSubmit: (values: Record<string, any>) => Promise<unknown>; onClose: () => void; wide?: boolean }) {
  const [v, setV] = useState<Record<string, any>>(() => {
    const o: Record<string, any> = {};
    fields.forEach((f) => (o[f.name] = initial[f.name] ?? (f.type === 'checkbox' ? false : '')));
    return o;
  });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [top, setTop] = useState('');
  const [busy, setBusy] = useState(false);

  function validate() {
    const e: Record<string, string> = {};
    for (const f of fields) {
      if (f.show && !f.show(v)) continue;
      const val = v[f.name];
      const empty = val === '' || val == null;
      if (f.required && f.type !== 'checkbox' && empty) e[f.name] = `${f.label} is required`;
      else if (!empty && f.type === 'number') {
        const n = Number(val);
        if (Number.isNaN(n)) e[f.name] = 'Enter a number';
        else if (f.min != null && n < f.min) e[f.name] = `Minimum is ${f.min}`;
        else if (f.max != null && n > f.max) e[f.name] = `Maximum is ${f.max}`;
      } else if (!empty && f.maxLength && String(val).length > f.maxLength) e[f.name] = `Max ${f.maxLength} characters`;
      else if (!empty && f.pattern && !f.pattern.test(String(val))) e[f.name] = f.patternMsg ?? 'Invalid format';
    }
    setErrs(e);
    return Object.keys(e).length === 0;
  }

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    setTop('');
    if (!validate()) return;
    const out: Record<string, any> = {};
    for (const f of fields) {
      if (f.show && !f.show(v)) continue;
      const val = v[f.name];
      if (f.type === 'checkbox') out[f.name] = !!val;
      else if (val === '' || val == null) continue;
      else out[f.name] = f.type === 'number' ? Number(val) : val;
    }
    setBusy(true);
    try {
      await onSubmit(out);
      onClose();
    } catch (e: any) {
      const se = e?.response?.data?.errors;
      if (se && typeof se === 'object') {
        const m: Record<string, string> = {};
        Object.entries(se).forEach(([k, x]: any) => (m[k] = Array.isArray(x) ? x[0] : String(x)));
        setErrs(m);
      }
      setTop(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={title} onClose={onClose} wide={wide}>
      <form onSubmit={submit} className="space-y-3">
        {top && <Notice kind="error">{top}</Notice>}
        {fields.filter((f) => !f.show || f.show(v)).map((f) => (
          <label key={f.name} className="block text-sm">
            {f.type === 'checkbox' ? (
              <span className="flex items-center gap-2">
                <input type="checkbox" checked={!!v[f.name]} onChange={(e) => setV({ ...v, [f.name]: e.target.checked })} /> {f.label}
              </span>
            ) : (
              <>
                <span className="block mb-1 font-medium">{f.label}{f.required && ' *'}</span>
                {f.type === 'select' ? (
                  <select className={inputCls} value={v[f.name]} onChange={(e) => setV({ ...v, [f.name]: e.target.value })}>
                    <option value="">Select…</option>
                    {f.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : f.type === 'textarea' ? (
                  <textarea className={inputCls} rows={3} value={v[f.name]} onChange={(e) => setV({ ...v, [f.name]: e.target.value })} />
                ) : f.type === 'file' ? (
                  <input type="file" className={inputCls} onChange={(e) => setV({ ...v, [f.name]: e.target.files?.[0] ?? '' })} />
                ) : (
                  <input type={f.type ?? 'text'} step={f.type === 'number' ? 'any' : undefined} className={inputCls} value={v[f.name]} onChange={(e) => setV({ ...v, [f.name]: e.target.value })} />
                )}
              </>
            )}
            {f.help && <span className="text-xs text-slate-500">{f.help}</span>}
            {errs[f.name] && <span className="block text-xs text-red-600 mt-1">{errs[f.name]}</span>}
          </label>
        ))}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy}>{busy ? 'Saving…' : submitLabel}</Button>
        </div>
      </form>
    </Modal>
  );
}

/** Confirm + run an async mutation; shows result/error banner. */
export function useAction(onDone?: () => void) {
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  async function run(fn: () => Promise<any>, opts: { confirm?: string; ok?: string } = {}) {
    if (opts.confirm && !(await askConfirm(opts.confirm))) return;
    setErr(''); setMsg(''); setBusy(true);
    try {
      const r = await fn();
      setMsg(opts.ok ?? r?.message ?? 'Done');
      onDone?.();
      return r;
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  }
  const banner = (
    <>
      {err && <Notice kind="error">{err}</Notice>}
      {msg && <Notice kind="ok">{msg}</Notice>}
    </>
  );
  return { run, err, msg, busy, banner, setErr, setMsg };
}

/** PIN-gated action. The idempotency key is reused on retry until the action succeeds. */
export function usePinAction(onDone?: () => void) {
  const [pending, setPending] = useState<null | ((pin: string, key: string) => Promise<any>)>(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const key = useRef(newKey());
  const ask = (fn: (pin: string, key: string) => Promise<any>) => { setErr(''); setMsg(''); setPending(() => fn); };
  const dialog = pending ? (
    <PinDialog
      onCancel={() => setPending(null)}
      onConfirm={async (pin) => {
        const fn = pending;
        setPending(null);
        try {
          const r = await fn(pin, key.current);
          key.current = newKey();
          setMsg(r?.message ?? 'Done');
          onDone?.();
        } catch (e) {
          setErr(errMsg(e));
        }
      }}
    />
  ) : null;
  const banner = (
    <>
      {err && <Notice kind="error">{err}</Notice>}
      {msg && <Notice kind="ok">{msg}</Notice>}
    </>
  );
  return { ask, dialog, banner, setErr, setMsg };
}

/* ---------- pickers / small widgets ---------- */
export const studentName = (s: any) => [s?.first_name, s?.last_name].filter(Boolean).join(' ') || s?.name || '—';

export function StudentPicker({ value, onPick, label = 'Student' }: { value?: any; onPick: (s: any | null) => void; label?: string }) {
  const [q, setQ] = useState('');
  const res = useQuery({ queryKey: ['studentpick', q], queryFn: () => call('get', '/school/students', { params: { q, per_page: 8 } }), enabled: q.length >= 2, retry: false });
  const rows = arr(res.data?.data);
  return (
    <div className="relative text-sm">
      <span className="block mb-1 font-medium">{label}</span>
      {value ? (
        <div className="flex items-center justify-between border rounded-xl px-3 py-2 bg-slate-50">
          <span>{studentName(value)} <span className="text-slate-500">· {value.admission_no}</span></span>
          <button type="button" className="text-brand-600" onClick={() => onPick(null)}>Change</button>
        </div>
      ) : (
        <>
          <input className={inputCls} placeholder="Search name or admission no…" value={q} onChange={(e) => setQ(e.target.value)} />
          {q.length >= 2 && (
            <div className="absolute z-10 mt-1 w-full bg-white border rounded-xl shadow max-h-56 overflow-y-auto">
              {res.isLoading && <p className="p-3 text-slate-500">Searching…</p>}
              {!res.isLoading && rows.length === 0 && <p className="p-3 text-slate-500">No matches</p>}
              {rows.map((s: any) => (
                <button type="button" key={s.id} className="block w-full text-left px-3 py-2 hover:bg-slate-50" onClick={() => { onPick(s); setQ(''); }}>
                  {studentName(s)} <span className="text-slate-500">· {s.admission_no}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: string }) {
  return (
    <div className="bg-white rounded-card border border-slate-200 p-4">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-xl font-bold text-brand-600">{value}</p>
      {sub && <p className="text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

export function Filters({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap gap-2 mb-3 items-end">{children}</div>;
}
export function Sel({ value, onChange, options, placeholder }: { value: string; onChange: (v: string) => void; options: { value: string | number; label: string }[]; placeholder: string }) {
  return (
    <select className="border rounded-xl px-3 py-2 text-sm bg-white" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
export function Search({ value, onChange, placeholder = 'Search…' }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  const [t, setT] = useState(value);
  useEffect(() => { const id = setTimeout(() => onChange(t), 350); return () => clearTimeout(id); }, [t]); // eslint-disable-line react-hooks/exhaustive-deps
  return <input className="border rounded-xl px-3 py-2 text-sm bg-white" placeholder={placeholder} value={t} onChange={(e) => setT(e.target.value)} />;
}
export function DateInput({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  return (
    <label className="text-xs text-slate-500">
      {label}
      <input type="date" className="block border rounded-xl px-3 py-2 text-sm bg-white text-slate-900" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}
export function KV({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm border-b border-slate-100">
      <span className="text-slate-500">{k}</span>
      <span className="text-right">{v}</span>
    </div>
  );
}
export const Link2 = ({ onClick, children }: { onClick: () => void; children: ReactNode }) => (
  <button type="button" onClick={(e) => { e.stopPropagation(); onClick(); }} className="text-brand-600 hover:underline text-sm">{children}</button>
);
